import {StoreEntry} from 'app/components/form/interfaces';
import {updateHashBasedOnUrl} from 'app/components/form/utils/updateHashBasedOnUrl';
import {gemStore} from 'app/gemStore';
import {EventKey, Nullable, Undef} from 'app/interface';
import {MODAL_SELECTOR, IS_HIDDEN_CLASSNAME, IS_MODAL_SHOW_CLASSNAME} from 'app/modules/modal/constants';
import {openModalAction, closeModalAction} from 'app/modules/modal/store/actions';
import {timeInSeconds} from 'app/themeVariables';
import {GemSettings} from 'app/utils/config/interface';
import {delegateEvent, triggerCustomEvent} from 'app/utils/jqueryNative';
import {previewOrEditWithMessage} from 'app/utils/previewOrEditMode';
import {HashHistory} from 'app/utils/urlState';
import {parseHashUrl} from 'app/utils/urlStateElements/parseHashUrl';
import {removeStatePartFromHash, urlify} from 'app/utils/urlStateElements/urlStateElements';
import {ModalConfiguration} from 'interfaces/foundation/rewriter/modal/modalConfiguration';

import {LinkTarget} from '../setHref';

import {ModalState, ModalThemeType, UrlModalState} from './interface';

const IS_DESTROYED_CLASSNAME = 'is-destroyed';
const DATA_MODAL_CONFIGURATION_ATTR = 'data-modal-configuration';

let previousModalUrl = '';
let previousUrlStatePath: Nullable<string> = '/';
let beforeModalOpenUrlStatePath: Nullable<string> = null;

export const initJSModal = (gemSettings: GemSettings) => () => {
    /**
     * @deprecated This is the legacy way of opening the modal
     */
    const removeOpenModalListener = delegateEvent(
        'click',
        '[data-modal], a[href*="/modalComponent/"]:not([data-tracking-cta-type="external"])',
        onModalClick(gemSettings),
    );

    const removeCloseModalListener = delegateEvent('click', '[data-modal-close]', (event) => {
        event.preventDefault();
        closeModalAction();
    });

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.code === EventKey.ESC) {
            const {modalComponent} = gemStore.getState();

            if (modalComponent.showCloseButton) {
                closeModalAction();
            }
        }
    };

    document.body.addEventListener('keydown', handleKeydown);

    const unsubscribeFromGemStore = gemStore.subscribe(() => {
        manageModalState(gemSettings);
        manageUrlState();
    });

    return () => {
        removeOpenModalListener();
        removeCloseModalListener();
        document.body.removeEventListener('keydown', handleKeydown);
        unsubscribeFromGemStore();
    };
};

document.body.style.setProperty('--SCROLLBAR-WIDTH', `${window.innerWidth - document.body.clientWidth}px`);

/**
 * @deprecated Used with delegateEvent
 * @param modalComponent
 * @param element
 */
function getNewModalState(modalComponent: Undef<UrlModalState>, element: HTMLAnchorElement) {
    if (!modalComponent?.url) {
        return;
    }

    const nextModalState: ModalState = {
        isOpen: true,
        url: decodeURIComponent(modalComponent.url),
        modalType: modalComponent.modalType,
    };

    if (modalComponent.additionalAttributes) {
        nextModalState.additionalAttributes = parseAdditionalAttributes(modalComponent.additionalAttributes);
    }

    const elementAdditionalAttributes = getAdditionalAttributes(element);
    if (elementAdditionalAttributes.length) {
        if (nextModalState.additionalAttributes) {
            nextModalState.additionalAttributes.push(...elementAdditionalAttributes);
        } else {
            nextModalState.additionalAttributes = elementAdditionalAttributes;
        }
    }

    let hash: Undef<string>;
    if (element.matches('[data-modal]')) {
        // in order to preserve old functionality - by default replace hash if modal is opened using data-modal attr
        hash = updateHashBasedOnUrl(nextModalState.url, false);
    } else {
        const currentHashState = parseHashUrl(HashHistory.getInstance().location.pathname);
        currentHashState.modalComponent = {isOpen: String(nextModalState.isOpen), url: modalComponent.url};
        hash = urlify(currentHashState);
    }

    nextModalState.hash = hash;

    const formAppScriptPath = getFormAppScriptPath(element);
    if (formAppScriptPath) {
        nextModalState.formAppScriptPath = formAppScriptPath;
    }

    return nextModalState;
}

/**
 * @deprecated Used with delegateEvent
 * @param gemSettings
 */
function onModalClick(gemSettings: GemSettings) {
    return async function (e: MouseEvent) {
        const url = this.getAttribute('href') ?? this.dataset.href ?? this.dataset.modalUrl;
        if (this.getAttribute('target') === LinkTarget.BLANK || this.dataset.hrefTarget === LinkTarget.BLANK) {
            return;
        }

        e.preventDefault();
        if (previewOrEditWithMessage('Modal', gemSettings)) {
            return;
        }

        beforeModalOpenUrlStatePath = window.location.hash;
        const {modalComponent}: {modalComponent?: UrlModalState} = parseHashUrl(url?.substring(1));
        const nextModalState = getNewModalState(modalComponent, this);
        if (!nextModalState) {
            return;
        }

        const {isOpen} = gemStore.getState().modalComponent;
        const modal = document.querySelector<HTMLDialogElement>(MODAL_SELECTOR);
        if (isOpen && modal) {
            try {
                await destroyModal(modal);
            } catch {
                return;
            }
        }

        openModalAction(nextModalState);
    };
}

function parseAdditionalAttributes(additionalAttributes: Undef<string>): Undef<StoreEntry[]> {
    try {
        if (additionalAttributes) {
            const data: StoreEntry[] = JSON.parse(decodeURIComponent(additionalAttributes));
            if (Array.isArray(data)) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Error while parsing additionalAttributes', e);
    }
}

/**
 * @deprecated Used with delegateEvent
 * @param element
 */
function getAdditionalAttributes(element: HTMLAnchorElement): StoreEntry[] {
    return Object.entries(element.dataset).reduce((storeEntries: StoreEntry[], [key, value]) => {
        if (key.length > 4 && key.startsWith('form') && typeof value === 'string') {
            const name = key.charAt(4).toLowerCase() + key.substring(5);
            storeEntries.push({name, value});
        }

        return storeEntries;
    }, []);
}

/**
 * Remove modal from body
 * @param modal
 */
function destroyModal(modal: HTMLDialogElement): Promise<void> {
    modal.classList.add(IS_HIDDEN_CLASSNAME);

    if (modal.classList.contains(IS_DESTROYED_CLASSNAME)) {
        return Promise.reject(new Error('Modal is already being destroyed'));
    }

    modal.classList.add(IS_DESTROYED_CLASSNAME);

    const cleanUp = () => {
        document.body.classList.remove(IS_MODAL_SHOW_CLASSNAME);
        triggerCustomEvent(modal, 'removeModal');
        modal.close();
        document.body.removeChild(modal);
    };

    if (process?.env?.NODE_ENV === 'test') {
        cleanUp();
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        setTimeout(() => {
            cleanUp();
            resolve();
        }, timeInSeconds.base * 1000);
    });
}

async function destroyModalAndUpdateURL(modal: HTMLDialogElement | null) {
    if (modal) {
        destroyModal(modal);

        const {getModalReactRoot} = await importModalHandler();
        getModalReactRoot()?.unmount();
    }

    if (beforeModalOpenUrlStatePath !== null) {
        HashHistory.getInstance().pushAndDispatch(beforeModalOpenUrlStatePath);
    } else {
        const hashState = parseHashUrl(HashHistory.getInstance().location.pathname);
        const path = removeStatePartFromHash(hashState, 'modalComponent', window.location.hash);
        HashHistory.getInstance().pushAndDispatch(path);
    }

    if (!window.location.hash) {
        HashHistory.getInstance().removeHashSilently();
    }
}

function manageModalState(gemSettings: GemSettings) {
    const {isOpen, url, modalType, callback, additionalAttributes, hash, formAppScriptPath, showCloseButton} = gemStore.getState().modalComponent;
    if (previousModalUrl === url) {
        return;
    }

    if (isOpen && previewOrEditWithMessage('Modal', gemSettings)) {
        closeModalAction();
        return;
    }

    previousModalUrl = url;
    const modal = document.querySelector<HTMLDialogElement>(MODAL_SELECTOR);
    if (isOpen === false) {
        // destroy modal
        destroyModalAndUpdateURL(modal);
    } else if (modal) {
        // load next content
        importModalHandler().then(({loadModalContent}) => {
            loadModalContent(modal, url, gemSettings, hash).then(() => {
                if (callback) {
                    callback();
                }
            });
        });
    } else {
        // create modal
        let type = modalType;
        if (document.body.classList.contains('skin-george')) {
            type = 'george' as ModalThemeType;
        }

        importModalHandler().then(({showModal}) => {
            showModal({url, callback, additionalAttributes, hash, formAppScriptPath, showCloseButton, modalType: type}, gemSettings);
        });
    }
}

function manageUrlState() {
    const {hashPath} = gemStore.getState().urlStateComponent;
    const {isOpen} = gemStore.getState().modalComponent;

    if (previousUrlStatePath === hashPath) {
        return;
    }

    previousUrlStatePath = hashPath;
    if (isOpen && hashPath === '/') {
        closeModalAction();
        return;
    }

    const newState = parseHashUrl(hashPath);
    const modalState = newState.modalComponent;
    if (modalState && 'isOpen' in modalState) {
        if (modalState.isOpen) {
            if (!modalState.url) {
                return;
            }

            const additionalAttributes = parseAdditionalAttributes(modalState.additionalAttributes);
            const url = decodeURIComponent(modalState.url);
            const formAppScriptPath = findFormAppScriptPath(modalState.url);
            // Remember prev hash state but without current modal URL
            beforeModalOpenUrlStatePath = removeStatePartFromHash(newState, 'modalComponent', HashHistory.getInstance().location.pathname);

            openModalAction({
                url,
                additionalAttributes,
                formAppScriptPath,
                modalType: modalState.modalType,
                hash: modalState.hash,
            });
        } else {
            closeModalAction();
        }
    }
}

function getFormAppScriptPath(link: HTMLAnchorElement): Undef<string> {
    const dataModalConfiguration = link.getAttribute(DATA_MODAL_CONFIGURATION_ATTR);
    if (!dataModalConfiguration) {
        return;
    }

    try {
        const modalConfiguration: ModalConfiguration = JSON.parse(dataModalConfiguration);
        return modalConfiguration.javaScriptPath;
    } catch (err) {
        console.warn(err);
    }
}

/**
 * Loop all links with data-modal-configuration attr to find javaScriptPath.
 * @param modalUrl
 */
function findFormAppScriptPath(modalUrl: string): Undef<string> {
    const link = Array.from(document.querySelectorAll<HTMLAnchorElement>(`a[${DATA_MODAL_CONFIGURATION_ATTR}]`)).find((item) => {
        const href = item.getAttribute('href');
        if (!href) {
            return;
        }

        const {modalComponent}: {modalComponent?: UrlModalState} = parseHashUrl(href.substring(1));
        return modalComponent?.url === modalUrl;
    });

    return link && getFormAppScriptPath(link);
}

function importModalHandler() {
    return import(/* webpackChunkName: "modalHandler" */ './handler');
}
